import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  IconButton,
  useTheme,
  Fade,
  Grow,
} from "@mui/material";
import {
  WorkspacePremium as PremiumIcon,
  Check as CheckIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
  Speed as SpeedIcon,
  ArrowForward as ArrowForwardIcon,
  Lock as LockIcon,
  Language as LanguageIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import lngs from "../../lngs";

const SubscriptionPrompt = () => {
  const { t, i18n } = useTranslation("subscription");
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [showContent, setShowContent] = useState(false);
  const [langAnchor, setLangAnchor] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleViewPlans = () => {
    history.push(`/${i18n.resolvedLanguage}/prices`);
  };

  const handleLogout = () => {
    dispatch({ type: "LOGOUT" });
    localStorage.removeItem("profile");
    localStorage.removeItem("hasActiveSubscription");
    history.push(`/${i18n.resolvedLanguage}/`);
  };

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    const newPath = location.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, `/${lng}`);
    history.replace(newPath);
    setLangAnchor(null);
  };

  const features = [
    {
      icon: <TrendingUpIcon />,
      title: t("advanced_analytics"),
      description: t("get_detailed_insights_into_your_business_performance"),
    },
    {
      icon: <SecurityIcon />,
      title: t("enhanced_security"),
      description: t("bank-grade_security_for_your_sensitive_data"),
    },
    {
      icon: <SupportIcon />,
      title: t("priority_support"),
      description: t("get_help_when_you_need_it_with_priority_support"),
    },
    {
      icon: <SpeedIcon />,
      title: t("faster_performance"),
      description: t("experience_lightning-fast_processing_speeds"),
    },
  ];

  const benefits = [
    t("unlimited_invoices_and_transactions"),
    t("advanced_reporting_and_analytics"),
    t("multi-user_collaboration"),
    t("priority_customer_support"),
    t("advanced_integrations"),
    t("custom_branding_options"),
  ];

  return (
    // Full-page background container
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        py: { xs: 4, md: 8 },
        px: { xs: 2, md: 4 },
        background: `linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #312e81 100%)`,
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 20% 20%, rgba(99,102,241,0.25) 0, transparent 40%), radial-gradient(circle at 80% 80%, rgba(236,72,153,0.20) 0, transparent 40%)",
          pointerEvents: "none",
        },
      }}
    >
      {/* Top toolbar — language switcher + logout (only on this page) */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: i18n.dir() === "rtl" ? "auto" : 16,
          left: i18n.dir() === "rtl" ? 16 : "auto",
          zIndex: 2,
          display: "flex",
          gap: 1,
        }}
      >
        <IconButton
          onClick={(e) => setLangAnchor(e.currentTarget)}
          sx={{
            color: "#fff",
            bgcolor: "rgba(255,255,255,0.12)",
            backdropFilter: "blur(10px)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
          }}
        >
          <LanguageIcon />
        </IconButton>
        <Menu
          anchorEl={langAnchor}
          open={Boolean(langAnchor)}
          onClose={() => setLangAnchor(null)}
        >
          {Object.keys(lngs).map((lng) => (
            <MenuItem
              key={lng}
              selected={lng === i18n.resolvedLanguage}
              onClick={() => handleLanguageChange(lng)}
            >
              {lngs[lng].nativeName}
            </MenuItem>
          ))}
        </Menu>

        <Button
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            color: "#fff",
            bgcolor: "rgba(255,255,255,0.12)",
            backdropFilter: "blur(10px)",
            textTransform: "none",
            px: 2,
            "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
          }}
        >
          {t("logout")}
        </Button>
      </Box>

      <Fade in={showContent} timeout={800}>
        {/* Glassmorphism content card */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: 1100,
            mx: "auto",
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            background: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 25px 70px rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.4)",
          }}
        >
          {/* Header Section */}
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Grow in={showContent} timeout={1000}>
              <Avatar
                sx={{
                  width: 96,
                  height: 96,
                  mx: "auto",
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  boxShadow: "0 10px 30px rgba(99,102,241,0.4)",
                }}
              >
                <PremiumIcon sx={{ fontSize: 52, color: "#fff" }} />
              </Avatar>
            </Grow>

            <Chip
              icon={<LockIcon sx={{ fontSize: 16 }} />}
              label={t("no_active_subscription")}
              color="warning"
              variant="filled"
              sx={{ mb: 3, fontWeight: 600 }}
            />

            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 800,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
              }}
            >
              {t("subscription_required")}
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 640, mx: "auto", fontWeight: 400 }}
            >
              {t("your_account_requires_an_active_subscription_to_continue_using_the_app.")}
            </Typography>
          </Box>

          {/* Features Grid */}
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in={showContent} timeout={1200 + index * 200}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 3,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: "0 14px 30px rgba(99,102,241,0.18)",
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: "center", p: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.primary.light,
                          color: theme.palette.primary.main,
                          width: 56,
                          height: 56,
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        {feature.icon}
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>

          {/* Benefits + CTA */}
          <Grid container spacing={4} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Grow in={showContent} timeout={1600}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 3,
                    height: "100%",
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                    {t("what_you'll_get")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    {benefits.map((benefit, i) => (
                      <ListItem key={i} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary={benefit}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grow>
            </Grid>

            <Grid item xs={12} md={6}>
              <Grow in={showContent} timeout={1800}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    height: "100%",
                    color: "#fff",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    boxShadow: "0 14px 40px rgba(99,102,241,0.35)",
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 800 }}>
                      {t("ready_to_upgrade?")}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.92 }}>
                      {t("join_thousands_of_businesses_already_using_our_premium_features_to_grow_their_success.")}
                    </Typography>

                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForwardIcon />}
                      onClick={handleViewPlans}
                      sx={{
                        py: 1.5,
                        px: 5,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1.1rem",
                        fontWeight: 700,
                        bgcolor: "#fff",
                        color: theme.palette.primary.main,
                        "&:hover": {
                          bgcolor: "#fff",
                          transform: "translateY(-2px)",
                          boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                        },
                      }}
                    >
                      {t("view_pricing_plans")}
                    </Button>

                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 2, opacity: 0.85 }}
                    >
                      {t("instant_access_after_payment")}
                    </Typography>
                  </Box>
                </Paper>
              </Grow>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Box>
  );
};

export default SubscriptionPrompt;
